import { useMutation } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { downloadFile } from '@/lib/download';

export type ExportFormat = 'xlsx' | 'csv' | 'pdf';

/** UI token → backend wire enum. The backend's enum is `csv`/`pdf`/`excel`; the UI calls the
 *  Excel option `xlsx` (the extension of the file it returns). Single source so the token sent
 *  on the wire can't drift from the menu/extension. */
const WIRE_FORMAT: Record<ExportFormat, string> = {
  xlsx: 'excel',
  csv: 'csv',
  pdf: 'pdf',
};

const EXTENSION: Record<ExportFormat, string> = {
  xlsx: 'xlsx',
  csv: 'csv',
  pdf: 'pdf',
};

export type ExportParams = {
  format: ExportFormat;
  /** Base filename without extension, e.g. "Components 2026-06-17". */
  filename: string;
  /** Arbitrary backend filter params merged into the export request body. */
  params?: Record<string, string | number | undefined>;
};

export function useExport(endpoint: string) {
  return useMutation({
    mutationFn: async ({ format, filename, params }: ExportParams) => {
      const body: Record<string, string | number | undefined> = { format: WIRE_FORMAT[format] };
      for (const [key, value] of Object.entries(params ?? {})) {
        if (value !== undefined && value !== '') body[key] = value;
      }
      const { url } = await apiClient.post<{ url: string }>(endpoint, body);
      downloadFile(url, `${filename}.${EXTENSION[format]}`);
    },
  });
}
